import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getAllBlogPosts, type BlogPost } from "@/lib/markdown";

const Blog: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBlogPosts = async () => {
      try {
        const blogPosts = await getAllBlogPosts();
        setPosts(blogPosts);
      } catch (error) {
        console.error("Error loading blog posts:", error);
      } finally {
        setLoading(false);
      }
    };

    loadBlogPosts();
  }, []);

  if (loading) {
    return (
      <div className="max-w-2xl">
        <h1 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 font-mono">
          Blog
        </h1>
        <div className="font-mono text-sm">Loading blog posts...</div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 font-mono">
        Blog
      </h1>

      <div className="mb-6 sm:mb-8 font-mono text-sm">
        <p>
          Thoughts on AI, research, education, and building things.
          {posts.length > 0 && ` ${posts.length} posts available.`}
        </p>
      </div>

      {posts.length === 0 ? (
        <div className="font-mono text-sm text-gray-600">
          No blog posts found.
        </div>
      ) : (
        <div className="space-y-6 sm:space-y-8">
          {posts.map((post, index) => (
            <article
              key={index}
              className="border-l-2 border-black pl-4 sm:pl-6"
            >
              <div className="text-xs font-mono text-gray-500 mb-2">
                {formatDate(post.date)}
              </div>

              <h2 className="text-base sm:text-lg font-bold font-mono mb-2">
                <Link
                  to={`/blog/${post.slug}`}
                  className="hover:bg-black hover:text-white px-1 py-1 transition-colors duration-200 inline-block"
                >
                  {post.title}
                </Link>
              </h2>

              <p className="text-sm font-mono text-gray-700 mb-3">
                {post.summary || "No summary available."}
              </p>

              {post.tags && post.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {post.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="text-xs font-mono border border-black px-2 py-1"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="text-xs font-mono">
                <Link to={`/blog/${post.slug}`} className="link-minimal">
                  Read More →
                </Link>
              </div>
            </article>
          ))}
        </div>
      )}

      <div className="mt-8 sm:mt-12 border-t border-black pt-6 sm:pt-8">
        <p className="font-mono text-sm text-gray-600">
          More posts coming soon. Subscribe to my{" "}
          <a href="#" className="link-minimal">
            RSS feed
          </a>{" "}
          or follow me on{" "}
          <a
            href="https://twitter.com"
            className="link-minimal"
            target="_blank"
            rel="noopener noreferrer"
          >
            Twitter
          </a>{" "}
          for updates.
        </p>
      </div>
    </div>
  );
};

export default Blog;
